<template>
  <div class="wrap">
    <header>
      <h1>LLM 模型与协议速查手册</h1>
      <div class="sub">命名后缀怎么读 · 四大协议怎么选 · 国内外主流厂商模型矩阵</div>
      <div class="meta">
        <span class="chip">整理时间：2026-09</span>
        <span class="chip">来源：各厂商开放平台模型广场</span>
        <span class="chip">覆盖：OpenAI / Claude / Grok / Gemini · Kimi / Qwen / DeepSeek / GLM / MiniMax / MiMo</span>
      </div>
    </header>

    <!-- 封面图：置于第一点内容上方 -->
    <div class="cover">
      <img src="https://img.cdn1.vip/i/6a97d9abbfb1e_1788336555.webp" alt="LLM 模型与协议速查手册封面" loading="lazy">
    </div>

    <!-- ========== 1 ========== -->
    <h2><span class="num">1</span>命名后缀速查：一眼看懂模型定位</h2>
    <p class="lead">模型名里的后缀不是花活，是厂商直接写给你的「能力 / 成本 / 延迟」说明书。后缀可叠加，例如 <code>gpt-5.3-Codex</code> = 中高配 + 面向 Agent 编程。</p>
    <div class="card">
      <div class="table-box">
        <table>
          <thead><tr><th style="width:180px;">后缀</th><th style="width:150px;">定位</th><th>什么时候用它</th><th style="width:150px;">成本 / 速度</th></tr></thead>
          <tbody>
            <tr>
              <td class="k">Pro / Max / Expert<br>Opus / Ultra</td>
              <td><span class="tag t-strong">最强档</span></td>
              <td>能力天花板，处理最复杂的工作：多步推理、长程规划、高难度编码</td>
              <td>最贵 · 最慢</td>
            </tr>
            <tr>
              <td class="k">thinking / reasoner<br>reasoning / -r</td>
              <td><span class="tag t-strong">深度思考</span></td>
              <td>大量逻辑推导、数学证明、复杂 Debug。显式开启思维链，思考时间长</td>
              <td>贵 · 慢（Token 消耗高）</td>
            </tr>
            <tr>
              <td class="k">Plus / Sonnet</td>
              <td><span class="tag t-bal">均衡档</span></td>
              <td>效果与成本最平衡，日常主力 / 默认选择</td>
              <td>中 · 中</td>
            </tr>
            <tr>
              <td class="k">mini / Flash / Fast<br>Lite / air / Haiku</td>
              <td><span class="tag t-fast">轻量档</span></td>
              <td>简单任务、高并发、实时交互、批量处理</td>
              <td>便宜 · 快</td>
            </tr>
            <tr>
              <td class="k">nano</td>
              <td><span class="tag t-mini">最小档</span></td>
              <td>极轻量任务：拼写纠错、简单分类、格式规整</td>
              <td>最便宜 · 最快</td>
            </tr>
            <tr>
              <td class="k">Coder / Codex / Code</td>
              <td><span class="tag t-cap">编程专用</span></td>
              <td>工具调用、环境交互、Agentic 长程编码任务</td>
              <td>视基座而定</td>
            </tr>
            <tr>
              <td class="k">VL</td>
              <td><span class="tag t-cap">视觉理解</span></td>
              <td>除文本外，可输入图像 / 视频做理解</td>
              <td>—</td>
            </tr>
            <tr>
              <td class="k">Omni</td>
              <td><span class="tag t-cap">全模态</span></td>
              <td>文 / 图 / 音 / 视 全支持</td>
              <td>—</td>
            </tr>
            <tr>
              <td class="k">instruct / chat</td>
              <td><span class="tag t-mini">对话向</span></td>
              <td>指令跟随、聊天对话场景</td>
              <td>—</td>
            </tr>
            <tr>
              <td class="k">preview</td>
              <td><span class="tag t-new">抢先版</span></td>
              <td>最新能力，但可能不稳定、接口可能变，别放生产核心链路</td>
              <td>—</td>
            </tr>
            <tr>
              <td class="k">oss</td>
              <td><span class="tag t-mini">开源</span></td>
              <td>Open Source Software，权重开放，可私有化部署</td>
              <td>—</td>
            </tr>
            <tr>
              <td class="k">search / web</td>
              <td><span class="tag t-cap">联网</span></td>
              <td>内置联网搜索能力，需要时效信息时用</td>
              <td>有额外检索计费</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ========== 2 ========== -->
    <h2><span class="num">2</span>四大协议：调用接口的标准之战</h2>
    <p class="lead">协议决定了你的代码怎么写、能不能一家 SDK 打天下。目前是 OpenAI 系与 Anthropic 系两强并立，其余为碎片化标准。</p>
    <div class="card">
      <div class="table-box">
        <table>
          <thead><tr><th style="width:130px;">协议</th><th style="width:110px;">出品方</th><th>一句话定位</th><th style="width:230px;">关键点</th></tr></thead>
          <tbody>
            <tr>
              <td class="k">completions</td>
              <td>OpenAI</td>
              <td><b>事实标准</b>。最古典、最通用、最主流的 LLM 对话调用接口</td>
              <td>兼容性最好的「最大公约数」，几乎所有厂商与网关都支持</td>
            </tr>
            <tr>
              <td class="k">messages</td>
              <td>Anthropic</td>
              <td>首个与 OpenAI 分庭抗礼的标准，刻意走差异化路由</td>
              <td>早期字段与 completions 很接近；<b>现已悄悄兼容 completions</b>；Claude Code 大幅提升其流行度</td>
            </tr>
            <tr>
              <td class="k">responses</td>
              <td>OpenAI</td>
              <td>面向 Agent 的<b>下一代 API 标准</b></td>
              <td>内置工具、有状态上下文，Codex 专属接口；chat 转 codex 可用 cpa 等工具，<b>性能可能有损失</b></td>
            </tr>
            <tr>
              <td class="k">X（各类）</td>
              <td>Google 等</td>
              <td>厂商自定义标准，把模型名、API 参数直接嵌进 URL</td>
              <td><span class="tag t-warn">奇形怪状</span> 各家写法不一，迁移成本高，建议用网关或 SDK 屏蔽</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="note blue">
      <b>实践建议：</b>新项目默认写 <code>completions</code>，兼容性最广；深度用 Claude / Claude Code 生态再上 <code>messages</code>；只有明确要用 Codex 的有状态与内置工具能力时才选 <code>responses</code>。遇到 X 类协议，优先用官方 SDK 或统一网关（如 One API / LiteLLM）做一层转换，别硬刚裸 URL。
    </div>

    <!-- ========== 3 ========== -->
    <h2><span class="num">3</span>国外厂商模型矩阵</h2>
    <p class="lead">右上角标签为该厂商支持的调用协议。</p>
    <div class="grid g2">

      <div class="vendor">
        <div class="hd"><b>OpenAI</b><span class="co">openai.com/api</span>
          <span class="proto"><span class="proto hl">completions</span> <span class="proto hl">responses</span></span>
        </div>
        <div class="bd">
          <div class="mrow"><div class="mname">gpt-5.5</div><div class="mdesc"><span class="tag t-strong">旗舰</span> 最新模型，处理最复杂的工作</div></div>
          <div class="mrow"><div class="mname">gpt-5.3-Codex</div><div class="mdesc"><span class="tag t-cap">Agentic</span> 擅长 agentic tasks，质量平衡，复杂开发任务</div></div>
          <div class="mrow"><div class="mname">gpt-5-mini</div><div class="mdesc"><span class="tag t-fast">主力</span> 适合大多数编码任务</div></div>
        </div>
      </div>

      <div class="vendor">
        <div class="hd"><b>Claude</b><span class="co">Anthropic</span>
          <span class="proto"><span class="proto hl">messages</span> <span class="proto">completions</span></span>
        </div>
        <div class="bd">
          <div class="mrow"><div class="mname">claude-opus-4-7</div><div class="mdesc"><span class="tag t-strong">最强</span> 处理最复杂逻辑推理</div></div>
          <div class="mrow"><div class="mname">claude-sonnet-4-6</div><div class="mdesc"><span class="tag t-bal">均衡</span> 体量适中，最平衡，日常主力</div></div>
          <div class="mrow"><div class="mname">claude-haiku-4-5-20251001</div><div class="mdesc"><span class="tag t-fast">轻量</span> 速度最快</div></div>
        </div>
      </div>

      <div class="vendor">
        <div class="hd"><b>Grok</b><span class="co">xAI · 马斯克</span>
          <span class="proto"><span class="proto hl">completions</span> <span class="proto hl">responses</span></span>
        </div>
        <div class="bd">
          <div class="mrow"><div class="mname">grok-4.3</div><div class="mdesc"><span class="tag t-strong">推荐</span> 官方强烈建议：除图视音场景外一律用它</div></div>
          <div class="mrow"><div class="mname">grok-4.20-0309-reasoning</div><div class="mdesc"><span class="tag t-strong">思考</span> 深度推理模式</div></div>
          <div class="mrow"><div class="mname">grok-4.20-0309-non-reasoning</div><div class="mdesc"><span class="tag t-bal">非思考</span> 速度优先</div></div>
        </div>
      </div>

      <div class="vendor">
        <div class="hd"><b>Gemini</b><span class="co">Google</span>
          <span class="proto"><span class="proto bad">x</span> <span class="proto">completions</span></span>
        </div>
        <div class="bd">
          <div class="mrow"><div class="mname">Gemini-3.1-Pro</div><div class="mdesc"><span class="tag t-strong">旗舰</span> 复杂任务</div></div>
          <div class="mrow"><div class="mname">Gemini-3-Flash</div><div class="mdesc"><span class="tag t-fast">轻量</span> 快速响应</div></div>
          <div class="mrow"><div class="mname">Gemini-3.1-Flash-Lite</div><div class="mdesc"><span class="tag t-mini">极轻</span> 成本与延迟最低</div></div>
        </div>
      </div>

    </div>

    <!-- ========== 4 ========== -->
    <h2><span class="num">4</span>国内厂商模型矩阵</h2>
    <p class="lead">国内厂商普遍「双协议」起步：completions + messages 已经是标配。</p>
    <div class="grid g2">

      <div class="vendor">
        <div class="hd"><b>Kimi</b><span class="co">月之暗面</span>
          <span class="proto"><span class="proto hl">completions</span> <span class="proto">messages</span></span>
        </div>
        <div class="bd">
          <div class="mrow"><div class="mname">kimi-k2.6</div><div class="mdesc"><span class="tag t-cap">Coding</span> 具备长程编码能力</div></div>
          <div class="mrow"><div class="mname">moonshot-v1</div><div class="mdesc"><span class="tag t-mini">简单</span> 简单文字任务</div></div>
        </div>
      </div>

      <div class="vendor">
        <div class="hd"><b>Qwen</b><span class="co">阿里 · 通义实验室</span>
          <span class="proto"><span class="proto hl">completions</span> <span class="proto hl">messages</span> <span class="proto hl">responses</span></span>
        </div>
        <div class="bd">
          <div class="mrow"><div class="mname">qwen3.6-max-preview</div><div class="mdesc"><span class="tag t-strong">旗舰</span> 抢先版，能力最强</div></div>
          <div class="mrow"><div class="mname">qwen3.6-plus</div><div class="mdesc"><span class="tag t-bal">均衡</span> 效果与成本平衡</div></div>
          <div class="mrow"><div class="mname">qwen3.6-flash</div><div class="mdesc"><span class="tag t-fast">轻量</span> 速度快</div></div>
          <div class="mrow"><div class="mname">qwen3-coder-plus</div><div class="mdesc"><span class="tag t-cap">Coding</span> 编程专用</div></div>
        </div>
      </div>

      <div class="vendor">
        <div class="hd"><b>DeepSeek</b><span class="co">深度求索</span>
          <span class="proto"><span class="proto hl">completions</span> <span class="proto">messages</span></span>
        </div>
        <div class="bd">
          <div class="mrow"><div class="mname">deepseek-v4-pro</div><div class="mdesc"><span class="tag t-strong">旗舰</span> 深度推理</div></div>
          <div class="mrow"><div class="mname">deepseek-v4-flash</div><div class="mdesc"><span class="tag t-fast">轻量</span> 快速响应</div></div>
          <div class="mrow"><div class="mname">reasoner</div><div class="mdesc"><span class="tag t-strong">思考</span> 深度思考、大量逻辑推导，编程用</div></div>
        </div>
      </div>

      <div class="vendor">
        <div class="hd"><b>GLM</b><span class="co">智谱 AI</span>
          <span class="proto"><span class="proto hl">completions</span> <span class="proto">messages</span></span>
        </div>
        <div class="bd">
          <div class="mrow"><div class="mname">glm-5.1</div><div class="mdesc"><span class="tag t-strong">旗舰</span> 官方称 coding 能力对齐 Claude-opus-4.6</div></div>
          <div class="mrow"><div class="mname">glm-5</div><div class="mdesc"><span class="tag t-bal">Agentic</span> coding 对齐 Claude-opus-4.5，擅长长程规划与执行</div></div>
          <div class="mrow"><div class="mname">glm-4.7</div><div class="mdesc"><span class="tag t-bal">通用</span> 对话、推理与智能体能力升级</div></div>
        </div>
      </div>

      <div class="vendor">
        <div class="hd"><b>MiniMax</b><span class="co">上海稀宇科技</span>
          <span class="proto"><span class="proto hl">completions</span> <span class="proto">messages</span></span>
        </div>
        <div class="bd">
          <div class="mrow"><div class="mname">MiniMax-M2.7</div><div class="mdesc"><span class="tag t-strong">旗舰</span> 官方称可自行构建复杂 Agent Harness</div></div>
          <div class="mrow"><div class="mname">MiniMax-M2.5</div><div class="mdesc"><span class="tag t-bal">次旗舰</span> 上一代主力</div></div>
        </div>
      </div>

      <div class="vendor">
        <div class="hd"><b>MiMo</b><span class="co">小米</span>
          <span class="proto"><span class="proto hl">completions</span> <span class="proto">messages</span></span>
        </div>
        <div class="bd">
          <div class="mrow"><div class="mname">MiMo-V2.5</div><div class="mdesc"><span class="tag t-strong">最新</span> 当前旗舰</div></div>
          <div class="mrow"><div class="mname">MiMo-V2</div><div class="mdesc"><span class="tag t-bal">上一代</span></div></div>
        </div>
      </div>

    </div>

    <div class="note">
      <b>⚠️ DeepSeek 迁移提醒：</b>现有的 <code>deepseek-chat</code> 与 <code>deepseek-reasoner</code> 两个模型名将<b>于日后弃用</b>。出于兼容考虑，二者分别对应 <code>deepseek-v4-flash</code> 的<b>非思考模式</b>与<b>思考模式</b>。新项目请直接按新命名接入。
    </div>
    <div class="note">
      <b>Qwen 参数量读法：</b>名字里的 <code>B</code> = Billion（十亿参数）。数字越大 → 参数量越大 → 能力越强、越耗算力。小参数（8B / 14B）→ 中等（32B / 72B）→ 大型（110B）。
      另有 <code>Embedding</code> 后缀表示<b>嵌入模型</b>，不直接对话，作用是把输入转成向量，用于知识库检索、语义相似度比对。
    </div>

    <!-- ========== 5 ========== -->
    <h2><span class="num">5</span>选型决策：四步走</h2>
    <p class="lead">按顺序回答四个问题，基本就能锁定该用哪个模型。</p>
    <div class="flow">
      <div class="step"><div class="q">① 任务复杂度？</div><div class="a">拼写纠错、分类、格式规整 → <b>nano / Flash-Lite / Haiku</b>；常规问答、改写、摘要 → <b>mini / Flash / Plus</b>；多步推理、长程规划、复杂 Debug → <b>Pro / Max / Opus</b></div></div>
      <div class="step"><div class="q">② 要不要显式推理？</div><div class="a">数学、证明、复杂逻辑链 → 开 <b>thinking / reasoner</b>；日常对话、低延迟场景 → 关闭思考模式，省 Token 也更快</div></div>
      <div class="step"><div class="q">③ 是不是编程？</div><div class="a">工具调用 / 环境交互 / 长程 Agent 编码 → <b>Coder / Codex / Code</b> 系列（gpt-5.3-Codex、qwen3-coder-plus、kimi-k2.6、glm-5.x）</div></div>
      <div class="step"><div class="q">④ 有没有非文本输入？</div><div class="a">图 / 视频理解 → <b>VL</b>；文图音视全都要 → <b>Omni</b>；需要实时信息 → <b>search / web</b></div></div>
    </div>

    <div class="note blue">
      <b>成本控制的通行做法：</b>把 <b>Plus / Flash 档作为默认主力</b>，只在置信度低或任务被判定为复杂时，才升级路由到 Pro / 思考模型。这一步「分级路由」通常能省下 60% 以上的开销，而体感质量几乎不掉。
    </div>

    <footer>
      本手册为公开模型文档的二次整理，模型迭代极快，接入前请回各厂商官方文档核对最新命名、价格与上下文长度。<br>
      整理时间 2026-09 · 共覆盖 10 家厂商、4 类协议、13 类命名后缀。
    </footer>
  </div>
</template>

<script>
export default {
  name: 'LLMKnowledge'
}
</script>

<style scoped>
  /* 语义变量映射到全局 token：改一处即可让整篇手册跟随主题（含暗色）。
     注意：scoped 样式里的 :root 选择器不生效，必须挂在 .wrap 上。 */
  .wrap {
    --bg: var(--surface-3);
    --card: var(--surface-1);
    --line: rgba(31, 45, 61, 0.08);
    --line-soft: rgba(31, 45, 61, 0.05);
    --text: var(--color-text-primary);
    --text-2: var(--color-text-regular);
    --text-3: var(--color-text-secondary);
    --accent: var(--color-primary);
    --accent-soft: var(--surface-2);
    --green: var(--color-success);
    --green-soft: var(--surface-2);
    --amber: var(--color-warning);
    --amber-soft: var(--surface-2);
    --red: var(--color-danger);
    --red-soft: var(--surface-2);
    --purple: var(--color-primary-dark);
    --purple-soft: var(--surface-2);
  }
  .wrap {
    /* 撑满内容区（居中限宽时左右留白由 margin 提供，内边距由内容区统一提供） */
    height: 100%;
    max-width: 1080px;
    margin: 0 auto;
    padding: 12px;
    background: var(--bg);
    color: var(--text);
    font-family: -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
    font-size: 14px;
    line-height: 1.7;
    -webkit-font-smoothing: antialiased;
    box-sizing: border-box;
    overflow: auto;
    border-radius: 8px;
  }
  .wrap * {
    box-sizing: border-box;
  }
  .wrap code {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    background: var(--line-soft);
    padding: 1px 5px;
    border-radius: 4px;
    font-size: 12.5px;
  }
  header {
    margin-bottom: 20px;
    padding-bottom: 24px;
    border-bottom: 1px solid rgba(31, 45, 61, 0.06);
  }
  h1 {
    font-size: 26px;
    font-weight: 700;
    margin: 0 0 8px;
    letter-spacing: -0.4px;
    color: var(--text);
  }
  .sub {
    color: var(--text-2);
    font-size: 14px;
  }
  .meta {
    margin-top: 14px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .chip {
    font-size: 12px;
    padding: 3px 10px;
    border-radius: 20px;
    background: var(--card);
    border: 1px solid var(--line);
    color: var(--text-2);
  }

  /* 封面图：置于第一点上方 */
  .cover {
    margin: 22px 0 6px;
    border-radius: 14px;
    overflow: hidden;
    border: 1px solid var(--line);
    background: var(--card);
    box-shadow: var(--shadow-surface-2);
  }
  .cover img {
    display: block;
    width: 100%;
    height: auto;
    max-height: 420px;
    object-fit: cover;
  }

  h2 {
    font-size: 19px;
    font-weight: 650;
    margin: 40px 0 6px;
    display: flex;
    align-items: center;
    gap: 10px;
    letter-spacing: -0.2px;
    color: var(--text);
  }
  h2 .num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 7px;
    background: var(--accent-soft);
    color: var(--accent);
    font-size: 13px;
    font-weight: 700;
    flex: none;
  }
  .lead {
    color: var(--text-2);
    margin: 0 0 16px;
    font-size: 13.5px;
  }

  .card {
    background: var(--card);
    border: 1px solid var(--line);
    border-radius: 12px;
    overflow: hidden;
  }
  .table-box {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13.5px;
  }
  thead th {
    background: var(--line-soft);
    text-align: left;
    font-weight: 600;
    color: var(--text-2);
    padding: 10px 14px;
    border-bottom: 1px solid rgba(31, 45, 61, 0.06);
    white-space: nowrap;
    font-size: 12.5px;
  }
  tbody td {
    padding: 11px 14px;
    border-bottom: 1px solid rgba(31, 45, 61, 0.06);
    vertical-align: top;
  }
  tbody tr:last-child td {
    border-bottom: none;
  }
  tbody tr:hover {
    background: var(--line-soft);
  }
  td.k {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12.5px;
    white-space: nowrap;
  }

  .tag {
    display: inline-block;
    font-size: 11.5px;
    padding: 2px 8px;
    border-radius: 5px;
    font-weight: 600;
    white-space: nowrap;
    line-height: 1.6;
  }
  .t-strong {
    background: var(--red-soft);
    color: var(--red);
  }
  .t-bal {
    background: var(--accent-soft);
    color: var(--accent);
  }
  .t-fast {
    background: var(--green-soft);
    color: var(--green);
  }
  .t-mini {
    background: var(--line-soft);
    color: var(--text-2);
  }
  .t-cap {
    background: var(--purple-soft);
    color: var(--purple);
  }
  .t-new {
    background: var(--amber-soft);
    color: var(--amber);
  }
  .t-warn {
    background: var(--red-soft);
    color: var(--red);
  }

  .grid {
    display: grid;
    gap: 14px;
  }
  .g2 {
    grid-template-columns: repeat(2, 1fr);
  }
  .vendor {
    background: var(--card);
    border: 1px solid var(--line);
    border-radius: 12px;
    padding: 0;
    overflow: hidden;
    min-width: 0;
  }
  .vendor .hd {
    padding: 13px 16px;
    border-bottom: 1px solid rgba(31, 45, 61, 0.06);
    display: flex;
    align-items: baseline;
    gap: 10px;
    flex-wrap: wrap;
    background: var(--line-soft);
  }
  .vendor .hd b {
    font-size: 15px;
    color: var(--text);
  }
  .vendor .hd .co {
    font-size: 12px;
    color: var(--text-3);
  }
  .vendor .hd .proto {
    margin-left: auto;
    display: flex;
    gap: 5px;
    flex-wrap: wrap;
  }
  .vendor .bd {
    padding: 4px 0;
  }
  .mrow {
    padding: 10px 16px;
    border-bottom: 1px solid rgba(31, 45, 61, 0.06);
    display: flex;
    gap: 12px;
    align-items: flex-start;
  }
  .mrow:last-child {
    border-bottom: none;
  }
  .mname {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12.5px;
    font-weight: 600;
    min-width: 210px;
    flex: none;
    color: var(--text);
  }
  .mdesc {
    color: var(--text-2);
    font-size: 13px;
  }
  .proto {
    font-size: 11px;
    font-family: ui-monospace, Menlo, monospace;
    padding: 2px 7px;
    border-radius: 4px;
    background: var(--line-soft);
    color: var(--text-2);
    font-weight: 600;
  }
  .proto.hl {
    background: var(--accent-soft);
    color: var(--accent);
  }
  .proto.bad {
    background: var(--red-soft);
    color: var(--red);
  }

  .note {
    background: var(--amber-soft);
    border: 1px solid var(--line);
    border-left: 3px solid var(--amber);
    border-radius: 0 8px 8px 0;
    padding: 12px 16px;
    font-size: 13px;
    color: var(--text-2);
    margin-top: 14px;
  }
  .note b {
    color: var(--text);
  }
  .note.blue {
    background: var(--accent-soft);
    border-color: var(--line);
    border-left-color: var(--accent);
    color: var(--text-2);
  }
  .note.blue b {
    color: var(--text);
  }

  .flow {
    display: flex;
    flex-direction: column;
    gap: 0;
    margin-top: 4px;
  }
  .step {
    display: flex;
    gap: 14px;
    align-items: flex-start;
    padding: 14px 16px;
    border: 1px solid var(--line);
    background: var(--card);
  }
  .step:first-child {
    border-radius: 12px 12px 0 0;
  }
  .step:last-child {
    border-radius: 0 0 12px 12px;
  }
  .step + .step {
    border-top: none;
  }
  .step .q {
    font-weight: 600;
    min-width: 150px;
    flex: none;
    font-size: 13.5px;
    color: var(--text);
  }
  .step .a {
    color: var(--text-2);
    font-size: 13px;
  }
  footer {
    margin-top: 44px;
    padding-top: 20px;
    border-top: 1px solid rgba(31, 45, 61, 0.06);
    color: var(--text-3);
    font-size: 12.5px;
  }

  @media (max-width: 820px) {
    .g2 {
      grid-template-columns: 1fr;
    }
  }
  @media (max-width: 640px) {
    .mrow {
      flex-direction: column;
      gap: 4px;
    }
    .mname {
      min-width: 0;
    }
    .step {
      flex-direction: column;
      gap: 3px;
    }
    .step .q {
      min-width: 0;
    }
    h1 {
      font-size: 22px;
    }
    h2 {
      font-size: 17px;
    }
  }
</style>
